import React, { useState } from 'react';
import { Container } from 'react-bootstrap';
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { testIconHelloPng } from '../../assets/images';
import { DoubleArrowIcon } from '../../components/DoubleArrowIcon';
import { SITE_LINKS } from '../../constants';
import { GoogleAnalyticsEvents } from '../../enum';
import { PlacementTestService } from '../../services/placement-test';
import { Background } from '../../stories/Background';
import { Header } from '../../stories/Header';
import { Icon } from '../../stories/Icon';
import { OnboardingNavbar } from '../../stories/OnboardingNavbar';
import { Panel } from '../../stories/Panel';

export interface PlacementTestPageProps {
  data: {
    token: string;
  };
}

export const PlacementTestPage: React.FC<PlacementTestPageProps> = (props) => {
  const __ = useTranslation().t;

  const [errorStateActive, setErrorStateActive] = useState<boolean>(false);
  const token = props.data?.token;

  const handleStartPlacementTest = () => {
    ReactGA.event(GoogleAnalyticsEvents.ViewPlacementTestInstructions);
    window.location.href = SITE_LINKS.PlacementTestIntroductionPage;
  };

  const handleSkipPlacementTest = () => {
    PlacementTestService.postSkipTest({ token })
      .then(() => {
        ReactGA.event(GoogleAnalyticsEvents.SkipPlacementTest);
        window.location.href = SITE_LINKS.PlacementTestResultPage;
      })
      .catch(() => {
        setErrorStateActive(true);
      });
  };

  return (
    <>
      <Header type="logo-only" />
      <Background>
        <Container>
          <div className="oejr-page-onboarding oejr-page-onboarding-4">
            <OnboardingNavbar
              numberOfCompletedSteps={2}
              totalSteps={3}
              backButtonHref={SITE_LINKS.OnboardingPage(`emergency-contact`)}
            />
            <StyleContainer>
              <StyledH1>{__('letsGetStarted')}</StyledH1>
              <OptionsContainer>
                <StyledCard onClick={handleSkipPlacementTest}>
                  <StyledLabel>
                    <img src={testIconHelloPng} alt="Icon" />
                    <StyleOptionTextContainer>
                      <StyledH2>{__('startAtLevelOne')}</StyledH2>
                      <OptionDescription>{__('startWithFoundation')}</OptionDescription>
                    </StyleOptionTextContainer>
                  </StyledLabel>
                  <StyledIcon name="arrow-right" />
                </StyledCard>
                <OptionDescription>
                  {__('beforeStartingYouCanTake')}
                  <StyledSpan onClick={handleStartPlacementTest}>
                    {__('placementTest')}
                    <DoubleArrowIcon size={30} />
                  </StyledSpan>
                </OptionDescription>
              </OptionsContainer>
            </StyleContainer>
            <Panel
              variant="danger"
              hidden={!errorStateActive}
              onHide={() => setErrorStateActive(false)}
            >
              <p>{__('opsCheckTyposContactUs')}</p>
            </Panel>
          </div>
        </Container>
      </Background>
    </>
  );
};

const StyleOptionTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
`;

const StyledH1 = styled.h1`
  font-family: '${({ theme }) => theme.fonts.primary.bold.fontFamily}';
  font-style: normal;
  line-height: 32px;
  color: ${({ theme }) => theme.colors.blue};
  text-align: center;
  margin-top: 60px;

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.xl}) {
    font-size: 24px !important;
    margin-top: 32px;
  }
`;

const StyleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6.25rem;
  @media screen and (max-width: ${({ theme }) => theme.screenSizes.xl}) {
    gap: 3.125rem;
  }
`;

const StyledIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.blue};
  height: 36px;
  font-weight: bold;
`;

const StyledH2 = styled.h2`
  font-family: '${({ theme }) => theme.fonts.primary.bold.fontFamily}';
  font-style: normal;
  font-weight: bold;
  line-height: 32px;
  text-align: left;
`;

const StyledSpan = styled.h3`
  font-family: '${({ theme }) => theme.fonts.primary.bold.fontFamily}';
  color: ${({ theme }) => theme.colors.blue};
  font-weight: bold;
  cursor: pointer;
  display: inline-flex;
  margin-left: 0.3rem;
  && svg {
    margin: auto;
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1.2rem;
  @media screen and (max-width: ${({ theme }) => theme.screenSizes.xl}) {
    gap: inherit;
  }
  img {
    width: 160px;
    height: 160px;
  }
`;

const OptionDescription = styled.p`
  font-size: 16px;
  line-height: 20px;
`;

const StyledCard = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.backgroundDark};
  border-radius: 12px;
  padding: 24px;
  font-family: ${({ theme }) => theme.fonts.secondary.regular.fontFamily};
  font-weight: 400;
  font-size: 20px;
  line-height: 32px;
  color: ${({ theme }) => theme.colors.dark};
  transition: all 0.3s ease;
  min-height: 208px;
  width: 566px;
  gap: 16px;
  input {
    display: none;
  }

  &:hover {
    box-shadow: 0px 3px 0px 1px ${({ theme }) => theme.colors.backgroundDark};
  }

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.xl}) {
    width: 100%;
    min-height: auto;
    flex-direction: column;
    gap: 40px;
    min-height: 208px;
  }
`;

const StyledLabel = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-weight: 400;
  font-size: 20px;
  line-height: 32px;
  gap: 40px;
  cursor: pointer;

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.xl}) {
    width: 100%;
    min-height: auto;
    flex-direction: column;
    gap: 16px;
  }
`;
